import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, DollarSign, CheckCircle2, Clock, Package, XCircle, Mail, Copy } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = [
  { v: "awaiting-payment", label: "Awaiting Zelle", icon: Clock, color: "bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)]" },
  { v: "paid",             label: "Paid",            icon: DollarSign, color: "bg-green-100 text-green-800" },
  { v: "fulfilling",       label: "Fulfilling",      icon: Package, color: "bg-blue-100 text-blue-800" },
  { v: "completed",        label: "Completed",       icon: CheckCircle2, color: "bg-emerald-100 text-emerald-800" },
  { v: "cancelled",        label: "Cancelled",       icon: XCircle, color: "bg-gray-200 text-gray-700" },
];

export default function PackageOrdersTab({ token }) {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/package-orders`, { headers: { "X-Admin-Password": token } });
      setList(data || []);
    } catch { toast({ title: "Could not load package orders", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const setStatus = async (order, status) => {
    try {
      await axios.patch(`${API}/admin/package-orders/${order.id}`, { status }, { headers: { "X-Admin-Password": token } });
      setList((l) => l.map((o) => (o.id === order.id ? { ...o, status } : o)));
      toast({ title: `Marked ${status}`, description: status === "paid" ? "Receipt emailed to customer." : "" });
    } catch { toast({ title: "Update failed", variant: "destructive" }); }
  };

  const awaiting = list.filter((o) => o.status === "awaiting-payment").length;
  const totalDue = list.filter((o) => o.status === "awaiting-payment").reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-6" data-testid="admin-packages-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Package orders</h2>
          <p className="text-sm text-[color:var(--dms-navy)]/60 mt-1">
            {awaiting > 0
              ? <><span className="text-[color:var(--dms-peach)] font-semibold">{awaiting} awaiting payment</span> · ${totalDue.toFixed(2)} owed</>
              : "All caught up"} · {list.length} total
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]" data-testid="admin-packages-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {list.length === 0 && !loading && (
          <div className="text-center py-16 text-[color:var(--dms-navy)]/50 text-sm" data-testid="admin-packages-empty">No package orders yet.</div>
        )}
        {list.map((o) => {
          const open = expanded === o.id;
          return (
            <div key={o.id} className="border border-[color:var(--dms-navy)]/10 rounded-xl overflow-hidden" data-testid={`admin-package-${o.id}`}>
              <button onClick={() => setExpanded(open ? null : o.id)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[color:var(--dms-cream)]/50 transition">
                <div className="flex items-center gap-4 min-w-0">
                  <StatusBadge status={o.status} />
                  <div className="min-w-0">
                    <p className="font-serif text-base text-[color:var(--dms-navy)] truncate">{o.package_name}</p>
                    <p className="text-xs text-[color:var(--dms-navy)]/60 truncate">{o.customer_name || "—"} · {o.customer_email}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-serif text-xl text-[color:var(--dms-navy)]">${(o.amount || 0).toFixed(2)}</p>
                  <p className="text-[11px] text-[color:var(--dms-navy)]/50 font-mono">{o.order_number}</p>
                </div>
              </button>

              {open && (
                <div className="px-5 pb-5 pt-2 border-t border-[color:var(--dms-navy)]/10 bg-[color:var(--dms-cream)]/40">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Row label="Business" value={o.business_name || "—"} />
                    <Row label="Phone" value={o.phone || "—"} />
                    <Row label="Billing" value={o.cadence || "—"} />
                    <Row label="Created" value={formatDate(o.created_at)} />
                    {o.notes && <div className="md:col-span-2"><Row label="Notes" value={o.notes} /></div>}
                  </div>

                  <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <a href={`mailto:${o.customer_email}?subject=Re:%20Your%20invoice%20${o.order_number}`} className="btn-peach" data-testid={`admin-package-reply-${o.id}`}>
                      <Mail className="w-4 h-4" /> Reply by email
                    </a>
                    <span className="text-xs text-[color:var(--dms-navy)]/50 mx-1">Set status:</span>
                    {STATUS_FLOW.map((s) => (
                      <button
                        key={s.v}
                        onClick={() => setStatus(o, s.v)}
                        disabled={o.status === s.v}
                        data-testid={`admin-package-status-${o.id}-${s.v}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] tracking-widest uppercase font-semibold transition ${o.status === s.v ? "bg-[color:var(--dms-navy)] text-white" : "bg-white border border-[color:var(--dms-navy)]/15 text-[color:var(--dms-navy)]/70 hover:border-[color:var(--dms-navy)]"}`}
                      >
                        <s.icon className="w-3 h-3" /> {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_FLOW.find((x) => x.v === status) || STATUS_FLOW[0];
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-widest uppercase font-semibold flex-shrink-0 ${s.color}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <p className="text-[10px] tracking-widest uppercase text-[color:var(--dms-navy)]/50 font-semibold">{label}</p>
      <p className="text-sm text-[color:var(--dms-navy)] mt-0.5 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
